import { lazyRouteComponent } from "@tanstack/react-router";
import { Lightbulb } from "lucide-react";

import type { PageDef } from "@/app/registry";

import type { UniverseKey } from "./lab";

export interface StockLabSearch {
  /** Ready-made idea key, or "custom" with `rule`. */
  idea: string;
  universe: UniverseKey;
  /** Investor-style filter on the idea cards (momentum, bounce, value, quality, custom). */
  style?: string;
  /** Your own rule in the scan language (idea = custom). */
  rule?: string;
  /** What-if overrides; absent means the idea's own setting. */
  stop?: number;
  target?: number;
  hold?: number;
  pos?: number;
  cap?: number;
}

const num = (v: unknown, ok: readonly number[]): number | undefined => {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return ok.includes(n) ? n : undefined;
};

export function parseStockLabSearch(s: Record<string, unknown>): StockLabSearch {
  const idea = typeof s.idea === "string" && /^[a-z0-9_]{1,40}$/.test(s.idea) ? s.idea : "market_leaders";
  const universe = s.universe === "nifty50" || s.universe === "nifty500" ? s.universe : "nifty100";
  const style = typeof s.style === "string" && /^[a-z]{1,20}$/.test(s.style) ? s.style : undefined;
  const rule = typeof s.rule === "string" && s.rule.trim() && s.rule.length <= 400 ? s.rule : undefined;
  const out: StockLabSearch = { idea: idea === "custom" && !rule ? "market_leaders" : idea, universe };
  if (style) out.style = style;
  if (rule && out.idea === "custom") out.rule = rule;
  const stop = num(s.stop, [0, 5, 8, 10, 15, 20]);
  const target = num(s.target, [0, 10, 15, 20, 30, 50]);
  const hold = num(s.hold, [0, 10, 20, 40, 60, 120, 250]);
  const pos = num(s.pos, [5, 10, 20]);
  const cap = num(s.cap, [100000, 500000, 2500000]);
  if (stop !== undefined) out.stop = stop;
  if (target !== undefined) out.target = target;
  if (hold !== undefined) out.hold = hold;
  if (pos !== undefined) out.pos = pos;
  if (cap !== undefined) out.cap = cap;
  return out;
}

export const pages: PageDef[] = [
  {
    path: "/app/stock-lab",
    proOnly: true,
    title: "Stock Strategy Lab",
    component: lazyRouteComponent(() => import("./StockLabPage"), "StockLabPage"),
    nav: { label: "Stock Strategy Lab", icon: Lightbulb, group: "Premium", order: 7 },
    validateSearch: parseStockLabSearch,
  },
];
